import { newestFirst } from '../mail-index.js';
import { canAssets } from '../assets-access.js';
import {
  openProjectsOnly, allEnquiries, outstandingInvoices, allClients, usersById,
  timesheetsForWeek, projectEmails, closedProjectCounts, teamTasks,
  updateProjectFields, addRecordChange, asDate, ACTIVE_STATUSES,
} from '../db.js';
import { dayKey } from '../tasks.js';
import { isUnknownV1 } from '../invoice-class.js';
import { orgSetting } from '../org.js';
import { financeFy27 } from '../fin-pilot.js';
import { waitingOnYou } from '../tasks-v2.js';
import { hasFeature } from '../features.js';
import { assetRegisterTasks } from '../assets-hooks-model.js';
import { offices, clockText } from '../header.js';
import { navIcon } from '../nav-icons.js';
import { escapeHtml, money, titleCase, refTail, secretFig, sentenceLabel } from '../ui.js';
import { hasKeys, keyInner } from '../keys.js';
import { timesheetRequired } from '../timesheet-required.js';

// HOME — v4.0.0 (Karan's design export 22/10/2026, p03;
// docs/ui-refresh/01-home-workspace.md §1).
//
// A greeting, then the one question the page answers: what needs you, most
// urgent first, each row with the verb that clears it. The right column is
// context — what's coming up, this week's figures, the latest email. No anchor
// numeral, no brief box, no stat cards: everything sits on hairlines.
//
// Every value is DERIVED from Firestore data — nothing here is hardcoded except
// the design's structure. Money rows only for Finance roles in a company with the
// Finance module, as before.

const DAY = 86400000;
const pad2 = (n) => String(n).padStart(2, '0');
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
function mondayOf(d) { const m = startOfDay(d); m.setDate(m.getDate() - ((m.getDay() + 6) % 7)); return m; }
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseHM = (v) => {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes(':')) { const [h, m] = s.split(':'); return (parseInt(h || 0, 10) * 60) + parseInt(m || 0, 10); }
  const f = parseFloat(s); return isNaN(f) ? 0 : Math.round(f * 60);
};
const hmFromMin = (min) => `${Math.floor(min / 60)}:${pad2(min % 60)}`;
const shortDate = (v) => { const d = asDate(v); return d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}` : '—'; };
const wkDate = (v) => { const d = asDate(v); return d ? `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${shortDate(d)}` : '—'; };
const senderName = (from) => {
  const f = String(from || '');
  return (f.match(/^"?([^"<]+?)"?\s*</) || [, ''])[1].trim() || f.split('<')[0].trim() || f || '—';
};
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** Good morning (before 12), afternoon (before 18), evening. */
export const greetingFor = (date = new Date()) => (date.getHours() < 12 ? 'Good morning' : date.getHours() < 18 ? 'Good afternoon' : 'Good evening');
/** "1 late · 1 to sign · 3 to check · 2 to chase" — empty clauses dropped. Review counts as a check. */
export function needsSummary(rows) {
  const n = (k) => rows.filter((r) => k.includes(r.kind)).length;
  return [[n(['late']), 'late'], [n(['sign']), 'to sign'], [n(['check', 'review']), 'to check'], [n(['money']), 'to chase']]
    .filter(([c]) => c).map(([c, w]) => `${c} ${w}`).join(' · ');
}

export async function renderHome(mount, session, { onOpenProject, onOpenRoute } = {}) {
  mount.innerHTML = '<div class="hm4-loading">Loading your morning…</div>';
  const isFinance = hasFeature('finance') && !!(session && Array.isArray(session.roles) && ['Owner', 'Director', 'Finance'].some((r) => session.roles.includes(r)));
  const canIssue = !!(session && Array.isArray(session.roles) && ['Owner', 'Director', 'Coordinator'].some((r) => session.roles.includes(r)));
  const meId = String((session && session.email) || '').split('@')[0];
  const now = new Date();
  const today = startOfDay(now);
  const horizon = new Date(today.getTime() + 14 * DAY);
  const weekStart = isoDay(mondayOf(now));

  const [projects, enquiries, invoices, clients, users, sheets, tasks] = await Promise.all([
    openProjectsOnly().catch(() => []),
    allEnquiries().catch(() => []),
    isFinance ? outstandingInvoices().catch(() => []) : [],
    allClients().catch(() => []),
    usersById().catch(() => ({})),
    timesheetsForWeek(weekStart).catch(() => []),
    teamTasks().catch(() => []),
  ]);

  const clientNameById = Object.fromEntries(clients.map((c) => [c.id, c.name]));
  const clientOf = (p) => p.clientName || clientNameById[refTail(p.clientRef)] || '—';
  const firstName = (id) => { const u = users[id]; return titleCase(String((u && u.displayName) || id || '').split(' ')[0]); };
  const leadOf = (p) => { const id = refTail(p.lead); return id ? firstName(id) : ''; };
  const daysSince = (v) => { const d = asDate(v); return d ? Math.max(0, Math.floor((today - startOfDay(d)) / DAY)) : 0; };

  const CLOSED = ['Complete', 'Issued'];
  const openProjects = projects.filter((p) => !CLOSED.includes(p.status));
  const liveProjects = projects.filter((p) => ACTIVE_STATUSES.includes(p.status));
  const overdue = openProjects.filter((p) => { const d = asDate(p.due); return d && d < today && p.status !== 'Ready to Issue'; });
  const toSign = projects.filter((p) => p.status === 'Ready to Issue');

  // ---- Needs you: typed rows, most urgent first ---------------------------
  const rows = [];
  for (const p of overdue.sort((a, b) => daysSince(b.due) - daysSince(a.due))) {
    const who = leadOf(p);
    const n = daysSince(p.due);
    rows.push({ kind: 'late', key: `p:${p.id}`, pid: p.id, num: p.number, title: `${clientOf(p)}${p.title ? ` — ${p.title}` : ''}`,
      meta: `At ${sentenceLabel(p.status || 'In progress')}${who ? ` with ${who}` : ''}`, when: `${plural(n, 'day')} past deadline`, late: true,
      verb: who ? `Ask ${who}` : 'Open', act: 'open' });
  }
  for (const p of toSign) {
    const who = leadOf(p);
    const d = asDate(p.due);
    const dn = d ? Math.round((startOfDay(d) - today) / DAY) : null;
    rows.push({ kind: 'sign', key: `p:${p.id}`, pid: p.id, num: p.number, title: `${clientOf(p)}${p.title ? ` — ${p.title}` : ''}`,
      meta: `Ready to issue${who ? `, with ${who}` : ''}`,
      when: dn == null ? '' : dn < 0 ? `${plural(-dn, 'day')} past deadline` : dn === 0 ? 'due today' : dn === 1 ? 'due tomorrow' : `due ${shortDate(d)}`,
      late: dn != null && dn < 0, verb: canIssue ? 'Sign and issue' : 'Open', act: canIssue ? 'issue' : 'open', primary: canIssue, status: p.status });
  }
  const projNo = Object.fromEntries(projects.map((p) => [p.id, p.number]));
  for (const t of waitingOnYou(tasks, meId, dayKey(now))) {
    const n = daysSince(t.reviewSince || t.updatedAt);
    rows.push({ kind: 'check', key: `t:${t.id}`, tid: t.id, pid: t.projectId || null, num: t.projectNo || projNo[t.projectId] || '', title: t.title || 'A task to check',
      meta: `From ${firstName(t.assigneeId || refTail(t.assignee) || '') || 'a colleague'}`, when: n ? `waiting ${plural(n, 'day')}` : 'today', late: n >= 2,
      verb: 'Check', act: 'task' });
  }
  // Assets mission 4: "Register 0142 · Dell Precision" — bought through Procurement, waiting on whoever ordered it.
  if (canAssets()) {
    for (const t of assetRegisterTasks(tasks, session)) {
      rows.push({ kind: 'check', key: `t:${t.id}`, tid: t.id, pid: null, num: t.assetId || t.sourceLabel || '', title: t.title || 'Register an asset',
        meta: 'Bought on a purchase order', when: '', late: false, verb: 'Register', act: 'asset', aid: t.assetId || '' });   // the row opens the asset (merge point)
    }
  }
  if (isFinance) {
    const v1Money = orgSetting('money', {}) || {};   // Unknown (V1) invoices are never chased (invoice-class.js)
    // A void or credited invoice is never chased (it isn't owed).
    const due = invoices.filter((i) => i.paymentStatus === 'Invoiced' && !i.v2Voided && i.status !== 'Credited' && !isUnknownV1(i, v1Money) && asDate(i.dueDate) && asDate(i.dueDate) < today)
      .sort((a, b) => asDate(a.dueDate) - asDate(b.dueDate));
    for (const i of due) {
      rows.push({ kind: 'money', key: `i:${i.id}`, num: i.number, title: `${i.clientName || clientNameById[refTail(i.clientRef)] || 'Invoice'} — invoice ${secretFig(money(Number(i.amount) || 0), true)}`,
        rawTitle: true, meta: `Due ${shortDate(i.dueDate)}`, when: `${plural(daysSince(i.dueDate), 'day')} overdue`, late: true, verb: 'Chase', act: 'invoices' });
    }
    for (const e of enquiries.filter((q) => q.feeDraft && !q.feeProposalIssuedAt && ['Enquiry', 'New', undefined, ''].includes(q.status))) {
      const n = daysSince(e.feeDraft.updatedAt || e.updatedAt);
      rows.push({ kind: 'review', key: `e:${e.id}`, eid: e.id, num: e.number, title: `Fee proposal — ${e.clientName || e.fromName || e.subject || 'an enquiry'}`,
        meta: 'Drafted by Track', when: n ? `${plural(n, 'day')} ago` : 'today', late: false, verb: 'Review', act: 'enquiry' });
    }
  }
  const needIds = new Set(rows.filter((r) => r.pid).map((r) => r.pid));

  // ---- Coming up: due inside a fortnight, not already above ---------------
  const coming = openProjects.filter((p) => { const d = asDate(p.due); return d && d >= today && d <= horizon && !needIds.has(p.id); })
    .sort((a, b) => asDate(a.due) - asDate(b.due)).slice(0, 5);

  // ---- This week ----------------------------------------------------------
  const weekMins = sheets.reduce((t, s) => t + (s.rows || []).reduce((x, r) => x + Object.values(r.days || {}).reduce((y, v) => y + parseHM(v), 0), 0), 0);
  // "N of M sheets in": M is the people who must file — not a room screen, not
  // someone whose timesheets are not required (Team, 26/10/2026) — and N counts
  // only their sheets, so a sheet Steve chose to file never reads "8 of 7".
  const teamList = Object.values(users).filter((u) => u.active !== false);
  const notRequired = new Set(teamList.filter((u) => !timesheetRequired(u)).map((u) => String(u.email || '').toLowerCase()).filter(Boolean));
  const teamSize = teamList.filter(timesheetRequired).length || sheets.length;
  const sheetsIn = sheets.filter((s) => ['submitted', 'approved', 'locked'].includes(s.status)
    && !notRequired.has(String(s.userEmail || '').toLowerCase())).length;
  const enqCount = (s) => enquiries.filter((e) => e.status === s).length;
  const pipe = [
    { n: enqCount('Enquiry'), label: 'Enquiry', tone: 'grey' },
    { n: enqCount('Quoted'), label: 'Quoted', tone: 'grey' },
    { n: projects.filter((p) => p.status === 'Not Started').length, label: 'Instructed', tone: 'blue' },
    { n: liveProjects.length, label: 'Worked on', tone: 'blue' },
    { n: null, label: 'Issued', tone: 'navy', pending: 'issued' },
    { n: null, label: 'Invoiced', tone: 'navy', pending: 'complete' },
  ];

  // ---- Latest email (the archiver's per-project index) --------------------
  const emailNums = [...new Set([...openProjects].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))).map((p) => p.number).filter(Boolean))].slice(0, 8);
  let emails = [];
  try { emails = newestFirst((await Promise.all(emailNums.map((no) => projectEmails(no).catch(() => [])))).flat()).slice(0, 3); } catch (_) { emails = []; }
  const emailTime = (v) => { const d = asDate(v); if (!d) return '—'; return startOfDay(d).getTime() === today.getTime() ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); };

  // ---- render -------------------------------------------------------------
  const name = titleCase(String((session && session.displayName) || meId).split(' ')[0]);
  const dateLine = `${now.toLocaleDateString('en-GB', { weekday: 'long' })} ${now.getDate()} ${now.toLocaleDateString('en-GB', { month: 'long' })} ${now.getFullYear()}`;
  const isMac = /Mac/.test(navigator.platform);
  const row = (r) => `
    <div class="hm4-row" data-key="${escapeHtml(r.key)}" data-kind="${r.kind}">
      <span class="kind-tag ${r.kind}">${escapeHtml({ late: 'Late', sign: 'Sign', check: 'Check', money: 'Money', review: 'Review' }[r.kind])}</span>
      <div class="hm4-row-text">
        <div class="hm4-row-title">${r.num ? `<span class="num-tech">${escapeHtml(String(r.num))}</span>` : ''}${r.rawTitle ? r.title : escapeHtml(r.title)}</div>
        <div class="hm4-row-meta">${escapeHtml(r.meta)}${r.when ? ` · <span class="${r.late ? 'late-text strong' : ''}">${escapeHtml(r.when)}</span>` : ''}</div>
      </div>
      <button type="button" class="btn sm${r.primary ? ' primary' : ''} hm4-verb" data-act="${r.act}">${escapeHtml(r.verb)}</button>
    </div>`;
  const summary = needsSummary(rows);

  mount.innerHTML = `
    <div class="hm4">
      <header class="hm4-head arrive-head">
        <div class="hm4-hello">
          <div class="hm4-date">${escapeHtml(dateLine)}</div>
          <h1 class="hm4-greet">${escapeHtml(greetingFor(now))}, ${escapeHtml(name)}</h1>
        </div>
        <div class="hm4-tools">
          <button type="button" class="hm4-search" id="hm4-search" aria-label="Search">
            <span class="hm4-search-ico">${navIcon('search', 14)}</span>
            <span class="hm4-search-ph">Search, or type “log 2h ${escapeHtml(String((liveProjects[0] && liveProjects[0].number) || '8601'))}”</span>
            ${hasKeys() ? `<kbd class="hm4-kbd">${keyInner('K')}</kbd>` : ''}
          </button>
          <div class="hm4-clocks" id="hm4-clocks"></div>
          <button type="button" class="btn icon hm4-bell" id="hm4-bell" aria-label="Notifications">${navIcon('bell', 16)}<span class="hm4-bell-dot" hidden></span></button>
        </div>
      </header>

      <div class="hm4-body">
        <section class="hm4-needs">
          <div class="hm4-needs-head">
            <h2 class="section-h">Needs you</h2>
            ${rows.length ? `<span class="hm4-count">${rows.length}</span>` : ''}
            <span class="hm4-summary">${escapeHtml(summary ? `${summary} — most urgent first` : '')}</span>
            ${isFinance && rows.some((r) => r.kind === 'money') ? `<div class="seg hm4-filter" role="tablist">
              <button type="button" class="active" data-f="all">All</button><button type="button" data-f="work">Work</button><button type="button" data-f="money">Money</button>
            </div>` : ''}
          </div>
          <div class="hm4-list arrive" id="hm4-list">${rows.map(row).join('')}</div>
          ${rows.length ? '' : '<div class="empty-state"><div class="empty-title">Nothing needs you right now</div><p>Late projects, things to sign, checks and overdue invoices land here, most urgent first.</p></div>'}
          <p class="hm4-foot">Done for today? Everything else is on <a href="#" data-go="projects">Projects</a> and <a href="#" data-go="tasks">Tasks</a>.</p>
        </section>

        <aside class="hm4-side">
          <section class="hm4-block">
            <div class="hm4-block-head"><h3>Coming up</h3><span class="hm4-aside">next 14 days</span></div>
            ${coming.length ? coming.map((p) => `<div class="hm4-coming" data-pid="${escapeHtml(p.id)}">
              <span class="num-tech hm4-coming-num">${escapeHtml(String(p.number || ''))}</span>
              <span class="hm4-coming-text"><span>${escapeHtml(clientOf(p))}</span><span class="hm4-sub">${escapeHtml(sentenceLabel(p.status || ''))}${leadOf(p) ? ` · ${escapeHtml(leadOf(p))}` : ''}</span></span>
              <span class="hm4-coming-date">${escapeHtml(wkDate(p.due))}</span>
            </div>`).join('') : '<p class="hm4-none">No deadlines in the next fortnight.</p>'}
          </section>

          <section class="hm4-block">
            <div class="hm4-block-head"><h3>This week</h3><a href="#" class="hm4-link" data-go="time">Timesheets</a></div>
            <div class="hm4-figs">
              <div><div class="fig-value">${escapeHtml(weekMins ? hmFromMin(weekMins) : '0:00')}</div><div class="fig-note">hours logged · ${sheetsIn} of ${teamSize} sheets in</div></div>
              <div><div class="fig-value">${liveProjects.length}</div><div class="fig-note">live projects</div></div>
            </div>
            <div class="hm4-pipe">${pipe.map((s) => `<div class="hm4-pipe-seg"><span class="hm4-bar ${s.tone} draws"></span>
              <span class="hm4-pipe-n num-tech"${s.pending ? ` data-pending="${s.pending}"` : ''}>${s.n == null ? '—' : s.n}</span><span class="hm4-pipe-l">${escapeHtml(s.label)}</span></div>`).join('')}</div>
          </section>

          <section class="hm4-block">
            <div class="hm4-block-head"><h3>Latest email</h3><a href="#" class="hm4-link" data-go="mail">Open Mail</a></div>
            ${emails.length ? emails.map((e) => `<div class="hm4-mail">
              <div class="hm4-mail-top"><span class="hm4-mail-from">${escapeHtml(senderName(e.from))}</span><span class="hm4-sub">${escapeHtml(emailTime(e.date))}</span></div>
              <div class="hm4-mail-subj">${escapeHtml(e.subject || '(no subject)')}</div>
            </div>`).join('') : '<p class="hm4-none">No project email indexed yet.</p>'}
          </section>
        </aside>
      </div>
    </div>`;

  // ---- behaviour ----------------------------------------------------------
  closedProjectCounts().then(({ issued, complete }) => {
    const set = (which, n) => { const el = mount.querySelector(`[data-pending="${which}"]`); if (el) el.textContent = String(n); };
    set('issued', issued); set('complete', complete);
  }).catch(() => {});

  const drawClocks = () => {
    const host = mount.querySelector('#hm4-clocks');
    if (!host || !host.isConnected) return false;
    host.innerHTML = offices().map((o) => `<span class="hm4-clock"><span>${escapeHtml(o.label)}</span> <b class="num-tech">${escapeHtml(clockText(o.tz))}</b></span>`).join('');
    return true;
  };
  drawClocks();
  const clockTimer = setInterval(() => { if (!drawClocks()) clearInterval(clockTimer); }, 30000);

  // Search: the ⌘K command bar (ask-bar.js listens for the shortcut).
  mount.querySelector('#hm4-search').addEventListener('click', () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: isMac, ctrlKey: !isMac, bubbles: true }));
  });
  // The bell opens the existing "needs you" sheet; a blue dot while it has rows.
  const pill = () => document.querySelector('.ntf-pill');
  const bell = mount.querySelector('#hm4-bell');
  bell.addEventListener('click', () => { const p = pill(); if (p) p.click(); });
  const dot = bell.querySelector('.hm4-bell-dot');
  // notifications.js announces the unread count; the dot shows while there is any.
  const onNeed = (ev) => { if (!bell.isConnected) { document.removeEventListener('track:need-you', onNeed); return; } dot.hidden = !(ev.detail && ev.detail.unread > 0); };
  document.addEventListener('track:need-you', onNeed);
  { const p = pill(); dot.hidden = !(p && !p.hidden); }

  const go = (route) => { if (onOpenRoute) onOpenRoute(route); };
  mount.querySelectorAll('[data-go]').forEach((a) => a.addEventListener('click', (ev) => { ev.preventDefault(); go(a.dataset.go); }));
  mount.querySelectorAll('.hm4-coming').forEach((r) => r.addEventListener('click', () => onOpenProject && onOpenProject(r.dataset.pid)));

  const filter = mount.querySelector('.hm4-filter');
  if (filter) filter.addEventListener('click', (ev) => {
    const b = ev.target.closest('button[data-f]'); if (!b) return;
    filter.querySelectorAll('button').forEach((x) => x.classList.toggle('active', x === b));
    mount.querySelectorAll('.hm4-row').forEach((r) => {
      const money = r.dataset.kind === 'money';
      r.hidden = b.dataset.f === 'money' ? !money : b.dataset.f === 'work' ? money : false;
    });
  });

  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
  const refreshCounts = () => {
    const left = rows.filter((r) => !r.done);
    const c = mount.querySelector('.hm4-count');
    if (c) { c.textContent = String(left.length); c.hidden = !left.length; }
    const s = needsSummary(left);
    mount.querySelector('.hm4-summary').textContent = s ? `${s} — most urgent first` : '';
  };
  // Chase: for the FY 2026–27 pilot, the pilot Invoices opens on its Overdue tab (the chasing screen).
  const goChase = () => {
    if (financeFy27(session && session.email)) { try { sessionStorage.setItem('track.invoices.tab', 'overdue'); } catch (_) { /* opens on the ledger */ } }
    go('invoices');
  };
  mount.querySelector('#hm4-list').addEventListener('click', async (ev) => {
    const el = ev.target.closest('.hm4-row'); if (!el) return;
    const r = byKey[el.dataset.key]; if (!r) return;
    const btn = ev.target.closest('.hm4-verb');
    if (!btn) { if (r.pid && onOpenProject) onOpenProject(r.pid); else if (r.act === 'invoices') goChase(); else if (r.act === 'enquiry') go(`enquiries/${r.eid}`); else if (r.act === 'task') go('tasks'); else if (r.act === 'asset' && r.aid) go(`assets/${r.aid}`); return; }
    if (r.act === 'issue') {
      // Fail conservative: the first press asks, the second issues. Forward-only (D2): Ready to Issue → Issued.
      if (!btn.dataset.confirm) { btn.dataset.confirm = '1'; btn.textContent = 'Confirm issue'; setTimeout(() => { if (btn.isConnected && btn.dataset.confirm && !btn.disabled) { delete btn.dataset.confirm; btn.textContent = r.verb; } }, 5000); return; }
      btn.disabled = true; btn.textContent = 'Issuing…';
      try {
        const at = new Date().toISOString();
        await updateProjectFields(r.pid, { status: 'Issued', statusUpdatedAt: at, statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at });
        await addRecordChange({ record: `projects/${r.pid}`, field: 'status', label: 'Status', from: r.status, to: 'Issued', by: session.email, byName: session.displayName || null, at, money: false }).catch(() => {});
        btn.classList.add('done'); btn.textContent = '✓ Issued';
        r.done = true;
        setTimeout(() => { el.classList.add('closing'); setTimeout(() => { el.remove(); refreshCounts(); }, 280); }, 700);
        const iss = mount.querySelector('[data-pending="issued"]'); if (iss && /^\d+$/.test(iss.textContent)) iss.textContent = String(Number(iss.textContent) + 1);
      } catch (e) {
        btn.disabled = false; delete btn.dataset.confirm; btn.textContent = r.verb;
        el.querySelector('.hm4-row-meta').insertAdjacentHTML('beforeend', ` · <span class="late-text">Couldn’t issue: ${escapeHtml(e.message || 'try again')}</span>`);
      }
      return;
    }
    if (r.act === 'open' && r.pid && onOpenProject) return onOpenProject(r.pid);
    if (r.act === 'task') return go('tasks');
    if (r.act === 'asset' && r.aid) return go(`assets/${r.aid}`);   // Assets: Register opens the asset
    if (r.act === 'invoices') return goChase();
    if (r.act === 'enquiry') return go(`enquiries/${r.eid}`);
  });
}
